// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { captureException } from "observability";
import { useTheme } from "@emotion/react";
import {
  Button,
  CryptoIcon,
  Dialog,
  EmptyState,
  FiatIcon,
  Flex,
  ListItem,
  SearchInput,
  SkeletonLoader,
  Text
} from "@hubble/web";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { fetchMarketFilters } from "utils/prices";
import { FixedHeightList } from "./styles";
import { FIAT_CURRENCIES } from "constants/converter";
import { checkForUSDStableCoin, isCryptoSymbol } from "../utils";
import CurrencySegmentSelector from "./CurrencySegmentSelector";
import { CURRENCY_SEGMENT_TYPES } from "./utils";

type SelectCurrencyModalProps = {
  onClose: () => void;
  isOpen: boolean;
  handleCurrencySelection?: (currency: string) => void;
  selectionMode?: SUPPORTED_CURRENCY_SELECTION_MODES;
  hiddenSymbols?: string[];
};

export enum SUPPORTED_CURRENCY_SELECTION_MODES {
  BASE_CURRENCY = "baseCurrency",
  QUOTE_CURRENCY = "quoteCurrency"
}

type CURRENCIES = {
  symbol: string;
  displayName: string;
};

const SelectCurrencyModal = ({
  onClose,
  handleCurrencySelection,
  isOpen,
  selectionMode,
  hiddenSymbols
}: SelectCurrencyModalProps) => {
  const t = useTranslations(FILES.CONVERTER);
  const { colorScheme } = useTheme();
  const [currencyState, setCurrencyState] = useState<{
    currencies: CURRENCIES[];
    isLoading: boolean;
    isError: boolean;
  }>({
    currencies: [],
    isLoading: true,
    isError: false
  });
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [selectedSegment, setSelectedSegment] = useState<
    (typeof CURRENCY_SEGMENT_TYPES)[keyof typeof CURRENCY_SEGMENT_TYPES]
  >(CURRENCY_SEGMENT_TYPES.CRYPTO);

  const handleCurrencyChange = useCallback((value: string) => {
    setSelectedSegment(value);
  }, []);

  const fetchSupportedCrypto = useCallback(async () => {
    setCurrencyState((prevState) => ({ ...prevState, isLoading: true }));
    try {
      const { filters, assets } = await fetchMarketFilters();
      const stableCoinsFilter =
        filters.find((_) => _.type === "StablecoinsMarketFilter")?.assets || [];

      // Filter out stable coins that are not USD pegged
      const stableCoins = stableCoinsFilter
        .map((coin) => ({
          to: coin.toUpperCase()
        }))
        .filter((coin) => checkForUSDStableCoin(coin.to));

      if (filters && assets) {
        // Assuming you want to set assets as currencies; adjust as needed
        setCurrencyState({
          currencies:
            assets
              ?.filter(
                (asset) =>
                  !!asset.lastTradePrice &&
                  !stableCoinsFilter.includes(asset.to)
              )
              // @ts-expect-error. It's comnbining an array of strings with an array of assets, but it seems to be ok because the code downstream is accounting for it. We should refactor this though
              ?.concat(...stableCoins)
              ?.sort((a, b) => {
                const priorityAssets = ["BTC", "ETH", "GUSD"];

                const aIndex = priorityAssets.indexOf(a.to);
                const bIndex = priorityAssets.indexOf(b.to);

                if (aIndex !== -1 && bIndex !== -1) {
                  // If both assets are in the priority list, sort them by their position in the list
                  return aIndex - bIndex;
                } else if (aIndex !== -1) {
                  // If only a is in the priority list, a comes first
                  return -1;
                } else if (bIndex !== -1) {
                  // If only b is in the priority list, b comes first
                  return 1;
                } else {
                  // If neither are in the priority list, sort them alphabetically
                  return a.to.localeCompare(b.to);
                }
              })
              ?.map((asset) => ({
                symbol: asset.to,
                displayName: asset.toDisplayName ?? asset.to
              })) ?? [],
          isLoading: false,
          isError: false
        });
      } else {
        throw new Error("Failed to fetch market filters");
      }
    } catch (error) {
      captureException(error as Error);
      setCurrencyState({
        currencies: [],
        isLoading: false,
        isError: true
      });
    }
  }, []);

  useEffect(() => {
    fetchSupportedCrypto();
  }, [fetchSupportedCrypto]);

  useEffect(() => {
    if (
      selectionMode === SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY &&
      selectedSegment === CURRENCY_SEGMENT_TYPES.CURRENCY
    ) {
      setSelectedSegment(CURRENCY_SEGMENT_TYPES.CRYPTO);
    }
  }, [selectedSegment, selectionMode]);

  // Handle search input changes
  const handleSearchValue = (value: string) => {
    setSearchValue(value);
  };

  // Clear search input
  const handleClearSearch = () => {
    setSearchValue("");
  };

  // Filter currencies based on search value, segment selected and selection mode
  const searchedRows = useMemo(() => {
    const performSearchFilteration = (currencies: CURRENCIES[]) => {
      return currencies.filter(
        (currency) =>
          currency.symbol.toLowerCase().includes(searchValue.toLowerCase()) ||
          currency.displayName.toLowerCase().includes(searchValue.toLowerCase())
      );
    };

    const processedFiatCurrencies = performSearchFilteration(
      FIAT_CURRENCIES.map((symbol) => ({
        symbol,
        displayName: symbol
      }))
    );

    const filteredCryptos = performSearchFilteration(currencyState.currencies);
    const filteredStableCoins = filteredCryptos.filter((coin) =>
      checkForUSDStableCoin(coin.symbol)
    );
    const filteredCryptosWithoutStableCoins = filteredCryptos.filter(
      (coin) => !checkForUSDStableCoin(coin.symbol)
    );

    let filteredCurrencies = [];

    if (selectionMode === SUPPORTED_CURRENCY_SELECTION_MODES.QUOTE_CURRENCY) {
      if (selectedSegment === CURRENCY_SEGMENT_TYPES.CRYPTO) {
        filteredCurrencies = filteredCryptosWithoutStableCoins;
      } else if (selectedSegment === CURRENCY_SEGMENT_TYPES.STABLECOINS) {
        filteredCurrencies = filteredStableCoins;
      } else if (selectedSegment === CURRENCY_SEGMENT_TYPES.CURRENCY) {
        filteredCurrencies = processedFiatCurrencies;
      }
    } else {
      if (selectedSegment === CURRENCY_SEGMENT_TYPES.CRYPTO) {
        filteredCurrencies = filteredCryptosWithoutStableCoins;
      } else if (selectedSegment === CURRENCY_SEGMENT_TYPES.STABLECOINS) {
        filteredCurrencies = filteredStableCoins;
      }
    }

    return filteredCurrencies.filter(
      (currency) => !hiddenSymbols?.includes(currency.symbol.toUpperCase())
    );
  }, [
    currencyState.currencies,
    selectionMode,
    searchValue,
    selectedSegment,
    hiddenSymbols
  ]);

  const renderIcon = (currencySymbol) => {
    return !isCryptoSymbol(currencySymbol) ? (
      <FiatIcon symbol={currencySymbol} />
    ) : (
      <CryptoIcon symbol={currencySymbol} />
    );
  };

  if (currencyState.isError) {
    return (
      <Dialog
        modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose?.();
          }
        }}
      >
        <Dialog.Container>
          <Dialog.Content>
            <EmptyState
              status="warning"
              label={t("somethingWentWrong")}
              description={t("unableToConnect")}
              cta={
                <Button.Secondary onClick={fetchSupportedCrypto}>
                  {t("tryAgain")}
                </Button.Secondary>
              }
            />
          </Dialog.Content>
        </Dialog.Container>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={isOpen}
      data-testid="select-asset-modal"
      onOpenChange={(open) => {
        setSearchValue("");
        if (!open) {
          onClose?.();
        }
      }}
    >
      <Dialog.Container>
        <Dialog.Header>
          <Dialog.Title>
            {t("selectCurrencyModalTitle", {
              name: (selectionMode ===
              SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
                ? t("crypto")
                : t("currency")
              ).toLowerCase()
            })}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          <React.Fragment>
            <CurrencySegmentSelector
              segmentTypes={
                (selectionMode ===
                SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
                  ? [
                      CURRENCY_SEGMENT_TYPES.CRYPTO,
                      CURRENCY_SEGMENT_TYPES.STABLECOINS
                    ]
                  : undefined) as (keyof typeof CURRENCY_SEGMENT_TYPES)[]
              }
              selectedValue={selectedSegment}
              onChange={handleCurrencyChange}
            />
            <SearchInput
              width="100%"
              name="search-currencies"
              type="string"
              value={searchValue}
              data-testid="searchable-currencies-input"
              onChange={({ target }) => handleSearchValue(target.value)}
              placeholder={t("searchPlaceholder")}
              onClear={handleClearSearch}
              mt={1}
              label={t("searchPlaceholder")}
            />

            {currencyState.isLoading ? (
              <React.Fragment>
                <SkeletonLoader
                  mt={1}
                  height="80px"
                  width="100%"
                  data-testid="select-currency-loading"
                />
                <SkeletonLoader mt={1} height="80px" width="100%" />
                <SkeletonLoader mt={1} height="80px" width="100%" />
                <SkeletonLoader mt={1} height="80px" width="100%" />
              </React.Fragment>
            ) : (
              <FixedHeightList>
                {searchedRows.map(({ symbol, displayName }) => {
                  return (
                    <ListItem
                      pl={0}
                      pr={0}
                      key={symbol}
                      data-testid={`currency-list-item-${symbol}`}
                      data-currency={symbol}
                      as="button"
                      onClick={() => {
                        setSearchValue("");
                        handleCurrencySelection(symbol);
                      }}
                      left={renderIcon(symbol)}
                      alignItems="center"
                    >
                      <Flex.Column align="flex-start">
                        {/* Display name handling, considering fiats may not have `toDisplayName` */}
                        <Text.Body size="md">{displayName}</Text.Body>
                        {/* Show the symbol for cryptos, but this might be redundant for fiats */}
                        {isCryptoSymbol(symbol) && (
                          <Text.Body size="sm">{symbol}</Text.Body>
                        )}
                      </Flex.Column>
                    </ListItem>
                  );
                })}
                {searchedRows.length === 0 && (
                  <Flex.Column align="center" as="li">
                    <Text.Body size="sm" color={colorScheme.content.secondary}>
                      {t("noResultsFound")}
                    </Text.Body>
                  </Flex.Column>
                )}
              </FixedHeightList>
            )}
          </React.Fragment>
        </Dialog.Content>
      </Dialog.Container>
    </Dialog>
  );
};

export default SelectCurrencyModal;
